// Story 7.12 Iteration 2 (ADO-911), AC7/AC9 + DR-5 — the Linked Product upsell seam.
//
// The PO's AC7 is "The Linked Product Modal will appear on click of Add to
// basket" and AC9 is "If there are no Linked Products the modal will not
// appear". Per Marika (2026-07-29) the modal fires on EVERY add-to-basket entry
// point — the PDP hero Add, quick-add, and add-from-listing (Story 7.9) — so it
// is driven by ONE standalone store action, `openUpsell(sku, name, variant)`,
// called AFTER a successful `useBasketStore.add`. Every add button wires to that
// single seam and there is exactly ONE host mounted, at the composition root.
//
// PRESENTATION VARIANT (ADO-911 design refresh, 2026-08-14). The same seam drives
// TWO presentations, chosen by the ADD's ORIGIN rather than the current route —
// the global search bar can add a product from any page, so route is not a
// reliable proxy for where the add came from. A PDP add opens the full,
// focus-trapped MODAL (unchanged); a search or listing add opens the
// non-invasive bottom-right TOAST. The origin travels with the call as
// `variant`, so a slow add settling after a navigation can never open the wrong
// presentation, and each add button declares which one it wants.
//
// AC6 — the upsell's cards are LINKED PRODUCTS (a set of SKUs sent from ERP), so
// they come from the FR106 "related" leg of `IRecommendationPort`, NOT the FR107
// co-purchase leg. (The design heading reads "Customers also bought these"; the
// content is Linked Products per AC6/AC7 — flagged for the PO as OQ-15.)
//
// AC9 is enforced HERE, not in the component: `openUpsell` resolves the SKU's
// linked products first and simply does not open when there are none. That keeps
// every call site a one-liner and makes "no linked ⇒ no modal" impossible to
// forget at a new trigger.

import { create } from 'zustand';
import { useRecommendationsStore } from '../recommendations/useRecommendationsStore';
import { recommendationsEntryAtSessionDepot } from '../recommendations/useRecommendationsEntry';

/** DR-5 — the upsell shows at most three Linked Product cards. */
export const MAX_UPSELL_CARDS = 3;

/** Which presentation an open upsell uses — the PDP dialog or the listing toast. */
export type UpsellVariant = 'modal' | 'toast';

interface UpsellModalState {
  /** The SKU whose add opened the upsell, or `null` when closed. */
  readonly openSku: string | null;
  /** The added product's display name, for the "{product} added to order" badge. */
  readonly openProductName: string | null;
  /** Which presentation to render while open, or `null` when closed. Set by the
   *  add's origin (PDP ⇒ `'modal'`, search/listing ⇒ `'toast'`). */
  readonly openVariant: UpsellVariant | null;
  /**
   * Open the Linked Product upsell for a product that was JUST added to the
   * basket, in the presentation the add's origin asked for (`variant`). Resolves
   * the product's linked products first and opens ONLY when there is at least
   * one (AC9) — a no-op otherwise. Safe to call from every add button; it never
   * throws (a failed recommendations fetch simply does not open the upsell, so a
   * background upsell can't break a successful add).
   */
  openUpsell: (sku: string, productName: string, variant: UpsellVariant) => Promise<void>;
  /** Dismiss (X / "No thanks" / Escape / scrim / navigating while OPEN). */
  close: () => void;
  /**
   * Supersede an `openUpsell` that is still awaiting its recommendations fetch,
   * WITHOUT touching the open state (there is none — this is the closed case).
   *
   * PR #168 review: `close()` covers navigate-AFTER-open, but the other ordering
   * was still live. `openUpsell` awaits a fetch before it commits, and nothing
   * bumped the generation on a navigation, so add-from-listing → click a card
   * title → the modal opened over the page the user had just landed on, focus
   * trap and scroll lock included, for a product on the previous screen. On a
   * cold SKU — every add outside the PDP, since only `PdpRecommendations`
   * pre-warms — that window is a full round trip, not a microtask.
   *
   * Called from `UpsellModal`'s location effect while `openSku === null`.
   */
  dismissPending: () => void;
  /** Test/session hygiene. */
  reset: () => void;
}

// A monotonic generation stamp, mirroring the sibling stores
// (`useRecommendationsStore`, `useProductDetailStore`). `openUpsell` is
// fire-and-forget (`useAddConfirmation` deliberately does not await it) and
// awaits a variable-latency fetch before committing, so without a stamp a
// SLOWER add could win: two quick adds would leave the badge naming one product
// while the cards belong to another, and a dismissal could be undone by a
// late-resolving call re-opening the dialog — potentially on a different page.
// Every `close()`/`reset()`/`dismissPending()` and every new `openUpsell` bumps
// it; a call only commits if it is still the newest.
let generation = 0;

export const useUpsellModalStore = create<UpsellModalState>((set) => ({
  openSku: null,
  openProductName: null,
  openVariant: null,

  async openUpsell(sku, productName, variant) {
    generation += 1;
    const gen = generation;
    // `load` resolves only once the SKU's entry has SETTLED — it joins an
    // in-flight fetch rather than returning early — so the AC9 read below can
    // never mistake "still loading" for "no linked products".
    await useRecommendationsStore.getState().load(sku);
    // Superseded while we waited (a newer add, or the user dismissed/navigated).
    if (gen !== generation) return;
    // DEPOT-SCOPED read (7-FU-I; @ADO-1222): a switch landing while we awaited `load()`
    // leaves an entry stamped with the depot the fetch STARTED at. Opening on that would
    // populate the upsell with the old depot's linked products, so a mismatch reads as no
    // entry and it simply doesn't open — the same outcome as no linked products.
    const related = recommendationsEntryAtSessionDepot(sku)?.related ?? [];
    // AC9 — no Linked Products (or the fetch errored → an empty entry) ⇒ no upsell.
    if (related.length === 0) return;
    set({ openSku: sku, openProductName: productName, openVariant: variant });
  },

  close() {
    generation += 1; // an in-flight openUpsell must not re-open what the user dismissed
    set({ openSku: null, openProductName: null, openVariant: null });
  },

  dismissPending() {
    // Generation ONLY — no `set`, so this cannot cause a render. Nothing is open
    // when it runs; all it does is make any pending `openUpsell` stale so it
    // cannot commit onto a page the user has already left.
    generation += 1;
  },

  reset() {
    generation += 1;
    set({ openSku: null, openProductName: null, openVariant: null });
  },
}));
